import React, { useState } from 'react';
import NumberPicker from 'react-widgets/NumberPicker';
import { Combobox } from 'react-widgets/cjs';
import { useGlobalActiveGeography } from '../../data/StatusStore';
import { useGlobalSwarmCalibration, onSetSwarmCalibration } from '../../data/GlobalStore';

interface RangeSwarmCalibrationPanelProps {
  title?: string;
}

const INFO: Record<string, string> = {
  segmentLengthPct: 'Horizontal length of each bar, as a % of total chart width.',
  collisionDiameter: 'Min horizontal distance between bar centers before the newer bar is pushed into the next row.',
  intervalBufferPct: 'Extra horizontal gap added on top of Bar Width when computing overlap — prevents edge-to-edge touching.',
  radius: 'Half the stroke thickness; controls how thick each bar looks and sets the base row step height.',
  rowSpacing: 'Extra vertical gap between stacked rows. 0 = rows touch; increase to spread them apart.',
  stackCount: 'Max rows a column can grow before the stack resets at that x position.',
  testEachPoint: 'TRUE: every bar tested vs the one immediately before it (denser). FALSE: only non-displaced bars are reference points (sparser).',
};

const RangeSwarmCalibrationPanel: React.FC<RangeSwarmCalibrationPanelProps> = ({ title = 'Range Swarm' }) => {
  const [openInfo, setOpenInfo] = useState<string | null>(null);
  const activeGeography = useGlobalActiveGeography();
  const swarmCalibration = useGlobalSwarmCalibration();
  const rangeCalibration = swarmCalibration?.RangeSwarm?.[activeGeography] ?? {
    radius: 1,
    padding: 0.25,
    pointDiameter: 0.75,
    collisionDiameter: 0.25,
    stackCount: 35,
    testEachPoint: 'FALSE',
    segmentLengthPct: 6,
    intervalBufferPct: 1.5,
  };

  const updateRangeCalibration = (patch: Record<string, any>) => {
    onSetSwarmCalibration({
      ...swarmCalibration,
      RangeSwarm: {
        ...(swarmCalibration?.RangeSwarm ?? {}),
        [activeGeography]: {
          ...rangeCalibration,
          ...patch,
        },
      },
    });
  };

  const InfoBtn = ({ k }: { k: string }) => (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpenInfo(openInfo === k ? null : k)}
        style={{ background: 'none', border: '1px solid #aaa', borderRadius: '50%', width: '12px', height: '12px', fontSize: '7px', lineHeight: '10px', cursor: 'pointer', padding: 0, marginLeft: '3px', verticalAlign: 'middle', color: '#555' }}
      >i</button>
      {openInfo === k && (
        <div style={{ position: 'absolute', left: '16px', top: '-2px', width: '160px', background: '#fff', border: '1px solid #aaa', borderRadius: '4px', padding: '5px 6px', fontSize: '9px', lineHeight: 1.4, zIndex: 999, color: '#333', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
          {INFO[k]}
        </div>
      )}
    </span>
  );

  return (
    <div style={{ width: '111px', backgroundColor: 'white', float: 'right' }}>
      <div style={{ width: '111px', fontSize: '8px', backgroundColor: 'white', fontWeight: 700 }}>{title}</div>

      <div style={{ fontSize: '8px' }}>Bar Width (%)<InfoBtn k="segmentLengthPct" /></div>
      <NumberPicker
        style={{ width: '111px', fontSize: '10px' }}
        value={rangeCalibration.segmentLengthPct}
        step={0.5}
        onChange={(e) => updateRangeCalibration({ segmentLengthPct: e })}
      />

      <div style={{ fontSize: '8px' }}>H. Collision (%)<InfoBtn k="collisionDiameter" /></div>
      <NumberPicker
        style={{ width: '111px', fontSize: '10px' }}
        value={rangeCalibration.collisionDiameter}
        step={activeGeography === 'Tract' ? 0.001 : 0.1}
        onChange={(e) => updateRangeCalibration({ collisionDiameter: e })}
      />

      <div style={{ fontSize: '8px' }}>H. Gap Buffer (%)<InfoBtn k="intervalBufferPct" /></div>
      <NumberPicker
        style={{ width: '111px', fontSize: '10px' }}
        value={rangeCalibration.intervalBufferPct ?? 1.5}
        step={0.5}
        onChange={(e) => updateRangeCalibration({ intervalBufferPct: e })}
      />

      <div style={{ fontSize: '8px' }}>Bar Thickness<InfoBtn k="radius" /></div>
      <NumberPicker
        style={{ width: '111px', fontSize: '10px' }}
        value={rangeCalibration.radius}
        step={activeGeography === 'Tract' ? 0.05 : 0.5}
        onChange={(e) => updateRangeCalibration({ radius: e })}
      />

      <div style={{ fontSize: '8px' }}>Row Spacing<InfoBtn k="rowSpacing" /></div>
      <NumberPicker
        style={{ width: '111px', fontSize: '10px' }}
        value={rangeCalibration.rowSpacing ?? 0}
        step={0.5}
        onChange={(e) => updateRangeCalibration({ rowSpacing: e })}
      />

      <div style={{ fontSize: '8px' }}>Max Rows<InfoBtn k="stackCount" /></div>
      <NumberPicker
        style={{ width: '111px', fontSize: '10px' }}
        value={rangeCalibration.stackCount}
        step={1}
        onChange={(e) => updateRangeCalibration({ stackCount: e })}
      />

      <div style={{ fontSize: '8px' }}>Test Each Point<InfoBtn k="testEachPoint" /></div>
      <Combobox
        defaultValue={rangeCalibration.testEachPoint ?? 'FALSE'}
        data={['TRUE', 'FALSE']}
        onChange={(e) => updateRangeCalibration({ testEachPoint: e })}
      />
    </div>
  );
};

export default RangeSwarmCalibrationPanel;